import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';

import { DatePicker } from 'primeng/datepicker';
import { ToggleSwitch } from 'primeng/toggleswitch';

import { TranslationModule } from '@core/translation/translation.module';
import { U2bDatePickerButtonBarComponent } from '@u2bUi/date-picker/u2b-date-picker-button-bar.component';
import { U2B_DATE_PICKER_CUSTOM_TIME_PT } from '@u2bUi/date-picker/u2b-date-picker.pt';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bDatePickerApplyDirective } from '@u2bUi/directives/u2b-date-picker-apply.directive';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

import {
    toReservationCheckInDefaultTime,
    toReservationValidUntilDefaultTime,
} from '@shared/functions/apply-reservation-default-times';

import { BookingV2BerthOccupancy, BookingV2BoatDimensions } from '../../dtos/booking-v2.dtos';
import { BookingV2Facade } from '../../services/booking-v2.facade';
import { BookingV2DimensionsComponent } from '../dimensions/booking-v2-dimensions.component';

// The berth step's top "current selection" area: shows the chosen boat and each
// selected berth. Every berth row is expandable to reveal its own Anreise/Abreise
// window (6609). Until expanded and edited, a berth's window mirrors the date-step
// range (`overridden === false`); editing a field pins it as an override.
@Component({
    selector: 'booking-v2-selection-summary',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        DatePicker,
        ToggleSwitch,
        TranslationModule,
        U2bDateMaskDirective,
        U2bDatePickerApplyDirective,
        U2bDatePickerButtonBarComponent,
        U2bIconButtonComponent,
        BookingV2DimensionsComponent,
    ],
    templateUrl: './selection-summary.component.html',
    host: { class: 'block' },
    // Recolor the disabled "open end" datepicker to the primary accent, matching
    // the date-range step's open-end field (the CSS vars cascade into the input).
    styles: [`
        .u2b-open-end-field {
            --p-inputtext-border-color: var(--p-primary-color);
            --p-inputtext-placeholder-color: var(--p-primary-color);
            --p-inputtext-disabled-color: var(--p-primary-color);
            --p-inputtext-disabled-background: color-mix(in srgb, var(--p-primary-color) 8%, transparent);
            --p-datepicker-dropdown-border-color: var(--p-primary-color);
            --p-datepicker-dropdown-color: var(--p-primary-color);
        }
    `],
})
export class SelectionSummaryComponent {

    private readonly facade = inject(BookingV2Facade);

    readonly boat = this.facade.selectedBoat;
    readonly occupancies = this.facade.berthOccupancies;

    /** Hides PrimeNG's read-only clock; the button bar renders a typable one. */
    readonly customTimePt = U2B_DATE_PICKER_CUSTOM_TIME_PT;

    // Dimensions of an active properties search, shown next to a real/new boat (7165).
    // The berth step only passes them while such a boat is selected.
    readonly searchDimensions = input<BookingV2BoatDimensions | null>(null);

    // Boat removal stays in the berth step (it also resets the find mode).
    readonly changeBoatRequested = output<void>();

    // Times of day a typed date falls back to while a window's field is empty — these windows
    // have no default-time correction of their own, so midnight would stick.
    readonly checkInFallbackTime = computed(
        () => toReservationCheckInDefaultTime(this.facade.defaultTimeUnit()));
    readonly checkOutFallbackTime = computed(
        () => toReservationValidUntilDefaultTime(this.facade.defaultTimeUnit()));

    // Which berth rows are expanded (by occupancy uuid).
    private readonly expandedIds = signal<ReadonlySet<string>>(new Set());

    constructor() {
        // Non-overridden windows follow the date-step range: re-sync them whenever
        // the booking range changes (e.g. the user went back and edited step 1).
        effect(() => {
            const range = this.facade.dateRange();
            untracked(() => {
                const list = this.facade.berthOccupancies();
                let changed = false;
                const next = list.map(o => {
                    if (o.overridden) {
                        return o;
                    }
                    if (o.from === range.from && o.to === range.to && o.openEnd === range.openEnd) {
                        return o;
                    }
                    changed = true;
                    return { ...o, from: range.from, to: range.to, openEnd: range.openEnd };
                });
                if (changed) {
                    this.facade.berthOccupancies.set(next);
                }
            });
        });
    }

    isExpanded(uuid: string): boolean {
        return this.expandedIds().has(uuid);
    }

    toggleExpanded(uuid: string): void {
        this.expandedIds.update(current => {
            const next = new Set(current);
            if (next.has(uuid)) {
                next.delete(uuid);
            } else {
                next.add(uuid);
            }
            return next;
        });
    }

    removeOccupancy(uuid: string): void {
        this.facade.berthOccupancies.update(list => list.filter(o => o.uuid !== uuid));
    }

    onFromChange(uuid: string, from: Date | null): void {
        this.patch(uuid, { from, overridden: true });
    }

    onToChange(uuid: string, to: Date | null): void {
        this.patch(uuid, { to, overridden: true });
    }

    onOpenEndChange(uuid: string, openEnd: boolean): void {
        // Open end clears the departure, mirroring the date step's toggle.
        this.patch(uuid, openEnd ? { openEnd, to: null, overridden: true } : { openEnd, overridden: true });
    }

    // Drop the override → the window snaps back to (and keeps tracking) the range.
    resetToDefault(uuid: string): void {
        const range = this.facade.dateRange();
        this.patch(uuid, { from: range.from, to: range.to, openEnd: range.openEnd, overridden: false });
    }

    formatDate(date: Date | null): string {
        return date && !isNaN(date.getTime())
            ? format(date, 'dd.MM.yyyy, HH:mm', { locale: de })
            : '';
    }

    private patch(uuid: string, patch: Partial<BookingV2BerthOccupancy>): void {
        this.facade.berthOccupancies.update(list =>
            list.map(o => (o.uuid === uuid ? { ...o, ...patch } : o)),
        );
    }
}
